import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiClient } from "../api/api";

export const useDataFetch = () => {
  const queryClient = useQueryClient();

  const authHeaders = {
    "Content-Type": "application/json",
    Authorization:   `${localStorage.getItem("token") || ""}`, 
    Accept: "application/json",
  };

  const useGetData = (
    queryKey,
    url,
    options = {},
    params = {},
    headers = {}
  ) => {
    return useQuery({
      queryKey,
      queryFn: async () => {
        try {
          const response = await apiClient.get(url, {
            params,
            headers: { ...headers, ...authHeaders },
          });
          if (response.status === 500) {
            throw new Error("Failed to fetch data");
          }
          return response?.data;
        } catch (error) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Error de conexión con el servidor";
          throw new Error(errorMessage);
        }
      },
      retry: 2,
      enabled: true,
      refetchOnWindowFocus: false,
      ...options,
    });
  };

  const usePostData = (
    url,
    headers = {},
    options = {}
  ) => {
    return useMutation({
      mutationFn: async (body) => {
        try {
          const response = await apiClient.post(url, body, {
            headers: { ...headers, ...authHeaders },
          });

          if (response.status === 500) {
            throw new Error("Failed to fetch data");
          } else if (response.status === 400) {
            throw new Error(response.data.message);
          }

          return response?.data;
        } catch (error) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Error al procesar la solicitud";
          throw new Error(errorMessage);
        }
      },
      ...options,
    });
  };

  const usePatchData = (
    url,
    headers = {},
    options = {}
  ) => {
    return useMutation({
      mutationFn: async (body) => {
        try {
          if (body.id) {
            url = `${url}/${body.id}`;
            delete body.id;
          }
          const response = await apiClient.patch(`${url}`, body, {
            headers: { ...headers, ...authHeaders },
          });

          if (response.status === 500) {
            throw new Error("Failed to fetch data");
          } else if (response.status === 400) {
            throw new Error(response.data.message);
          }

          return response?.data;
        } catch (error) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Error al actualizar el registro";
          throw new Error(errorMessage);
        }
      },
      ...options,
    });
  };

  const usePutData = (
    url,
    headers = {},
    options = {}
  ) => {
    return useMutation({
      mutationFn: async (body) => {
        try {
          let targetUrl = url;
          if (body?.id) {
            targetUrl = `${url}/${body.id}`;
            const { id, ...rest } = body;
            body = rest;
          }

          const response = await apiClient.put(targetUrl, body, {
            headers: { ...headers, ...authHeaders },
          });

          if (response.status === 500) {
            throw new Error("Failed to fetch data");
          } else if (response.status === 400) {
            throw new Error(response.data.message);
          }

          return response?.data;
        } catch (error) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Error al actualizar el registro";
          throw new Error(errorMessage);
        }
      },
      ...options,
    });
  };

  const useDeleteData = (
    url,
    headers = {},
    options = {}
  ) => {
    return useMutation({
      mutationFn: async (id) => {
        try {
          const response = await apiClient.delete(id ? `${url}/${id}` : url, {
            headers: { ...headers, ...authHeaders },
          });

          if (response.status === 500) {
            throw new Error("Failed to fetch data");
          }

          return response?.data;
        } catch (error) {
          const errorMessage =
            error?.response?.data?.message ||
            error?.message ||
            "Error al eliminar el registro";
          throw new Error(errorMessage);
        }
      },
      ...options,
    });
  };

  const invalidateQuery = (queryKey) => {
    queryClient.invalidateQueries({ queryKey });
  };

  return {
    getData: useGetData,
    postData: usePostData,
    patchData : usePatchData,
    putData: usePutData,
    deleteData: useDeleteData,
    invalidateQuery,
  };
};
